import { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { Server, Router, Cpu, Database, HardDrive, Wifi } from 'lucide-react';
import { Card } from '@/components/ui/card';

interface EndpointNodeData {
  id: string;
  label: string;
  category?: string;
  ip?: string;
}

interface EndpointNodeProps {
  data: EndpointNodeData;
  selected: boolean;
}

const getIcon = (category?: string) => {
  if (category?.includes('Router') || category?.includes('Switch')) return <Router className="h-3 w-3" />;
  if (category?.includes('PLC') || category?.includes('HMI')) return <Cpu className="h-3 w-3" />;
  if (category?.includes('DB') || category?.includes('Database')) return <Database className="h-3 w-3" />;
  if (category?.includes('Storage') || category?.includes('NAS')) return <HardDrive className="h-3 w-3" />;
  if (category?.includes('AP') || category?.includes('Wi-Fi')) return <Wifi className="h-3 w-3" />;
  return <Server className="h-3 w-3" />;
};

export const EndpointNode = memo(({ data, selected }: EndpointNodeProps) => {
  return (
    <>
      <Card className={`min-w-[120px] ${selected ? 'ring-2 ring-primary shadow-lg' : ''}`}>
        <div className="p-2">
          <div className="flex items-center gap-1.5">
            {getIcon(data.category)}
            <div className="flex-1 min-w-0">
              <div className="font-medium text-xs truncate">{data.label}</div>
              {data.ip && <div className="text-xs text-muted-foreground truncate">{data.ip}</div>}
            </div>
          </div>
        </div>
      </Card>

      {/* 4 bidirectional handles - consistent with other nodes */}
      <Handle 
        type="source" 
        position={Position.Top} 
        id="top"
        isConnectableStart={true}
        isConnectableEnd={true}
        className="w-2 h-2 border-2 border-blue-500 bg-blue-100 dark:bg-blue-800" 
      />
      <Handle 
        type="source" 
        position={Position.Right} 
        id="right"
        isConnectableStart={true}
        isConnectableEnd={true}
        className="w-2 h-2 border-2 border-blue-500 bg-blue-100 dark:bg-blue-800" 
      />
      <Handle 
        type="source" 
        position={Position.Bottom} 
        id="bottom"
        isConnectableStart={true}
        isConnectableEnd={true}
        className="w-2 h-2 border-2 border-blue-500 bg-blue-100 dark:bg-blue-800" 
      />
      <Handle 
        type="source" 
        position={Position.Left} 
        id="left"
        isConnectableStart={true}
        isConnectableEnd={true}
        className="w-2 h-2 border-2 border-blue-500 bg-blue-100 dark:bg-blue-800" 
      />
    </>
  );
});

EndpointNode.displayName = 'EndpointNode';
